import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { keyframes } from 'tss-react'
import { WidgetAnimationDirection } from './types'
import { J1RightarrowD } from '@e3dc-react/icons'
import { Link, useNavigate } from 'react-router-dom'
import { WIDGET_HEIGHT } from './const'

interface StyleProps {
  stateOfCharge: number
  animationDirection?: WidgetAnimationDirection
  hasLink: boolean
  progressBarColor?: string
  progressBarStripesColor?: string
}

const move = keyframes`
      0% { background-position: 0 0 }
      100% { background-position: 50px 50px }
    `
const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  container: {
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.effectStyles.shadowMain,
    backgroundColor: theme.palette.background.paper,
    minHeight: WIDGET_HEIGHT,
    minWidth: 200,
    alignItems: 'stretch',
    flex: '1 1 200px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    cursor: props.hasLink ? 'pointer' : 'default',

    [theme.breakpoints.down('sm')]: {
      minWidth: 'unset',
      maxHeight: 'unset',
      minHeight: 'unset',
    },
  },
  progressbarWrapper: {
    backgroundColor: theme.palette.grey.grey07,
    borderRadius: theme.shape.customBorderRadius.progressBar,
    height: 36,
    position: 'relative',
  },
  progressbar: {
    width: `${props.stateOfCharge}%`,
    height: '100%',
    backgroundColor: props.progressBarColor || theme.palette.primary.light,
    borderRadius: theme.shape.customBorderRadius.progressBar,
    position: 'relative',
    overflow: 'hidden',
  },
  animate: {
    backgroundColor: props.progressBarColor || theme.palette.primary.light,
    backgroundImage: `linear-gradient(-45deg, ${
      props.progressBarStripesColor || theme.palette.primary.main
    }50 25%, transparent 25%, transparent 50%, ${props.progressBarStripesColor || theme.palette.primary.main}50 50%, ${
      props.progressBarStripesColor || theme.palette.primary.main
    }50 75%, transparent 75%, transparent)`,
    backgroundSize: ' 50px 50px',
    animation: `${move} 3s linear infinite`,
    animationDirection: props.animationDirection === 'right' ? 'normal' : 'reverse',
    borderRadius: `${theme.shape.customBorderRadius.progressBar}px`,
  },
  offline: {
    backgroundColor: theme.palette.grey.grey06,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
  },
}))

interface WidgetBaseProps {
  title: string
  subtitle?: React.ReactNode
  children?: React.ReactNode
  stateOfCharge: number
  animationDirection?: WidgetAnimationDirection
  link?: string
  isOffline?: boolean
  toggleButton?: React.ReactNode
  dataQa?: string
  progressBarColor?: string
  progressBarStripesColor?: string
}

const WidgetBase: React.FC<WidgetBaseProps> = ({
  title,
  subtitle,
  link,
  stateOfCharge,
  animationDirection,
  isOffline,
  children,
  toggleButton,
  dataQa,
  progressBarColor,
  progressBarStripesColor,
}) => {
  const { cx, classes } = useStyles({ stateOfCharge, animationDirection, hasLink: !!link, progressBarColor, progressBarStripesColor })
  const theme = useTheme()

  const IsDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const navigate = useNavigate()
  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (link) {
      event.preventDefault()
      navigate(link)
    }
  }

  return (
    <Box className={classes.container} data-qa={dataQa} onClick={handleClick}>
      <Box display={'flex'} justifyContent={'space-between'}>
        <Box display={'flex'} flexDirection={'column'} justifyContent={'center'}>
          <Typography variant="h3" sx={{ wordBreak: 'break-word' }}>
            {title}
          </Typography>
          {subtitle}
        </Box>
        <Box className={classes.controls}>
          {!!toggleButton && toggleButton}
          {link && !IsDownSm && (
            <Link to={link}>
              <IconButton sx={{ color: 'text.primary' }}>
                <J1RightarrowD />
              </IconButton>
            </Link>
          )}
        </Box>
      </Box>
      {!isOffline && <Box>{children}</Box>}
      <Box className={classes.progressbarWrapper}>
        <Box className={cx(classes.progressbar, { [classes.animate]: !!animationDirection && !isOffline, [classes.offline]: isOffline })} />
      </Box>
    </Box>
  )
}

export default WidgetBase
